import { baseApi as api } from "../baseApi";
const injectedRtkApi = api.injectEndpoints({
  endpoints: (build) => ({
    getV2ProviderAvailabilityRules: build.query<
      GetV2ProviderAvailabilityRulesApiResponse,
      GetV2ProviderAvailabilityRulesApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/provider/availability/rules`,
        params: {
          page: queryArg.page,
          per_page: queryArg.perPage,
          provider_id: queryArg.providerId,
        },
      }),
    }),
    postV2ProviderAvailabilityRules: build.mutation<
      PostV2ProviderAvailabilityRulesApiResponse,
      PostV2ProviderAvailabilityRulesApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/provider/availability/rules`,
        method: "POST",
        body: queryArg.createAvailabilityRuleRequest,
      }),
    }),
    deleteV2ProviderAvailabilityRulesByAvailabilityRuleId: build.mutation<
      DeleteV2ProviderAvailabilityRulesByAvailabilityRuleIdApiResponse,
      DeleteV2ProviderAvailabilityRulesByAvailabilityRuleIdApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/provider/availability/rules/${queryArg.availabilityRuleId}`,
        method: "DELETE",
      }),
    }),
    patchV2ProviderAvailabilityRulesByAvailabilityRuleId: build.mutation<
      PatchV2ProviderAvailabilityRulesByAvailabilityRuleIdApiResponse,
      PatchV2ProviderAvailabilityRulesByAvailabilityRuleIdApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/provider/availability/rules/${queryArg.availabilityRuleId}`,
        method: "PATCH",
        body: queryArg.updateAvailabilityRuleRequest,
      }),
    }),
  }),
  overrideExisting: false,
});
export { injectedRtkApi as enhancedApi };
export type GetV2ProviderAvailabilityRulesApiResponse =
  /** status 200 OK */ ListResponseAvailabilityRuleSchema;
export type GetV2ProviderAvailabilityRulesApiArg = {
  /** Page number (1-based) */
  page?: number;
  /** Items per page (max 1000) */
  perPage?: number;
  /** Filter to a provider's rules (the calendar owner user id). */
  providerId?: string | null;
};
export type PostV2ProviderAvailabilityRulesApiResponse =
  /** status 200 OK */ AvailabilityRuleResponse;
export type PostV2ProviderAvailabilityRulesApiArg = {
  createAvailabilityRuleRequest: CreateAvailabilityRuleRequest;
};
export type DeleteV2ProviderAvailabilityRulesByAvailabilityRuleIdApiResponse =
  unknown;
export type DeleteV2ProviderAvailabilityRulesByAvailabilityRuleIdApiArg = {
  availabilityRuleId: string;
};
export type PatchV2ProviderAvailabilityRulesByAvailabilityRuleIdApiResponse =
  /** status 200 OK */ AvailabilityRuleResponse;
export type PatchV2ProviderAvailabilityRulesByAvailabilityRuleIdApiArg = {
  availabilityRuleId: string;
  updateAvailabilityRuleRequest: UpdateAvailabilityRuleRequest;
};
export type AvailabilityRuleSchema = {
  availability_rule_id?: string | null;
  created_at?: string | null;
  /** Window length in minutes. */
  duration_minutes: number;
  /** 'open' (bookable) or 'block' (unavailable). */
  effect: string;
  /** Encounter type the window applies to; null = all types. */
  encounter_type_id?: string | null;
  /** Recurrence bound; null = open-ended. */
  end_date?: string | null;
  organization_id: string;
  /** Calendar owner (user id); null means org-wide. */
  provider_id?: string | null;
  /** RFC-5545 recurrence; null = one-off. */
  rrule?: string | null;
  /** 'user' or 'organization' (derived from provider_id). */
  scope: string;
  /** First occurrence start (date + time-of-day). */
  start_time: string;
  /** IANA timezone the window was authored in. */
  timezone: string;
  updated_at?: string | null;
};
export type ListResponseAvailabilityRuleSchema = {
  items: AvailabilityRuleSchema[];
  page?: number;
  per_page?: number;
  total?: number;
  total_pages?: number;
};
export type ValidationErrorElement = {
  ctx?: object;
  loc: string[];
  msg: string;
  type: string;
};
export type ValidationError = ValidationErrorElement[];
export type AvailabilityRuleSchema2 = {
  availability_rule_id?: string | null;
  created_at?: string | null;
  /** Window length in minutes. */
  duration_minutes: number;
  /** 'open' (bookable) or 'block' (unavailable). */
  effect: string;
  /** Encounter type the window applies to; null = all types. */
  encounter_type_id?: string | null;
  /** Recurrence bound; null = open-ended. */
  end_date?: string | null;
  organization_id: string;
  /** Calendar owner (user id); null means org-wide. */
  provider_id?: string | null;
  /** RFC-5545 recurrence; null = one-off. */
  rrule?: string | null;
  /** 'user' or 'organization' (derived from provider_id). */
  scope: string;
  /** First occurrence start (date + time-of-day). */
  start_time: string;
  /** IANA timezone the window was authored in. */
  timezone: string;
  updated_at?: string | null;
};
export type AvailabilityRuleResponse = {
  rule: AvailabilityRuleSchema2;
};
export type ClientErrorResponse = {
  /** The error code for the error */
  error_code?: string;
  /** The message for the error */
  message?: string;
  /** The HTTP status code for the client error */
  status?: number;
};
export type CreateAvailabilityRuleRequest = {
  /** Window length in minutes. */
  duration_minutes: number;
  /** 'open' (bookable) or 'block' (unavailable). */
  effect: string;
  /** Restrict to one encounter type; null = all types. */
  encounter_type_id?: string | null;
  /** Recurrence bound; omit for open-ended. */
  end_date?: string | null;
  /** Calendar owner; omit/null to create an org-wide rule. */
  provider_id?: string | null;
  /** RFC-5545 recurrence rule; omit for a one-off window. */
  rrule?: string | null;
  /** First occurrence start (date + time-of-day). */
  start_time: string;
  /** IANA timezone the window is authored in. */
  timezone: string;
};
export type UpdateAvailabilityRuleRequest = {
  duration_minutes?: number | null;
  effect?: string | null;
  encounter_type_id?: string | null;
  end_date?: string | null;
  provider_id?: string | null;
  rrule?: string | null;
  start_time?: string | null;
  timezone?: string | null;
};
